import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from './supabase';
import { t } from './translations';
import JarSVG from './JarSVG';

// ─── DAILY JAR PAGE ──────────────────────────────────────────────────────────
// Shows ONE random favorite (or any) glow per day. 
// Tracks last-opened date in localStorage so it resets at midnight.

function todayKey() {
  return 'mgm_daily_' + new Date().toISOString().slice(0, 10);
}

function Particles() {
  const ps = Array.from({length:6},(_,i)=>({id:i,size:40+Math.random()*70,left:Math.random()*100,delay:Math.random()*8,dur:12+Math.random()*10}));
  return (
    <div className="bg-particles">
      {ps.map(p=><div key={p.id} className="particle" style={{width:p.size,height:p.size,left:`${p.left}%`,animationDelay:`${p.delay}s`,animationDuration:`${p.dur}s`}}/>)}
    </div>
  );
}

export default function DailyJarPage({ lang, setLang, user, onLogout }) {
  const T = t[lang];
  const navigate = useNavigate();
  const [phase, setPhase] = useState('closed'); // closed | opening | open | empty
  const [todayGlow, setTodayGlow] = useState(null);
  const [alreadyOpened, setAlreadyOpened] = useState(false);
  const [loading, setLoading] = useState(true);
  const [jarFill, setJarFill] = useState(0.7);

  useEffect(() => { if (!user) navigate('/login'); }, [user, navigate]);

  useEffect(() => {
    if (!user) return;
    async function load() {
      setLoading(true);
      const { data: prof } = await supabase.from('profiles').select('jar_id').eq('id', user.id).single();
      if (!prof) { setLoading(false); return; }

      // Fetch all messages
      const { data: msgs } = await supabase
        .from('messages').select('*').eq('jar_id', prof.jar_id).eq('reported', false);

      if (!msgs || msgs.length === 0) { setPhase('empty'); setLoading(false); return; }

      setJarFill(Math.min(msgs.length / 100, 1));

      // Check if already opened today
      const stored = localStorage.getItem(todayKey());
      if (stored) {
        const glow = msgs.find(m => m.id === stored) || msgs[Math.floor(Math.random() * msgs.length)];
        setTodayGlow(glow);
        setAlreadyOpened(true);
        setPhase('open');
      } else {
        // Prefer favorites, then unread, then any
        const pool = msgs.filter(m => m.favorited).length > 0
          ? msgs.filter(m => m.favorited)
          : msgs.filter(m => !m.read).length > 0
          ? msgs.filter(m => !m.read)
          : msgs;
        const pick = pool[Math.floor(Math.random() * pool.length)];
        setTodayGlow(pick);
        setPhase('closed');
      }
      setLoading(false);
    }
    load();
  }, [user]);

  async function openJar() {
    if (!todayGlow) return;
    setPhase('opening');
    localStorage.setItem(todayKey(), todayGlow.id);
    // Mark as read
    await supabase.from('messages').update({ read: true }).eq('id', todayGlow.id);
    setTimeout(() => setPhase('open'), 900);
  }

  const isMoodGood = phase === 'open' && todayGlow;

  if (loading) return (
    <><Particles/>
    <div className="page" dir={T.dir} style={{fontFamily:T.font}}>
      <DailyNav lang={lang} setLang={setLang} user={user} onLogout={onLogout} navigate={navigate}/>
      <div className="center-page"><div className="center-icon">🫙</div></div>
    </div></>
  );

  return (
    <><Particles/>
    <div className="page" dir={T.dir} style={{fontFamily:T.font}}>
      <DailyNav lang={lang} setLang={setLang} user={user} onLogout={onLogout} navigate={navigate}/>

      <div style={{maxWidth:520,margin:'0 auto',padding:'40px 20px',textAlign:'center'}}>

        {/* Date badge */}
        <div style={{fontSize:'0.75rem',color:'var(--text-muted)',letterSpacing:'0.1em',textTransform:'uppercase',marginBottom:32}}>
          {new Date().toLocaleDateString(lang==='ar'?'ar-EG':'en-GB',{weekday:'long',day:'numeric',month:'long'})}
        </div>

        {phase === 'empty' && (
          <>
            <div style={{fontSize:'4rem',marginBottom:20,opacity:0.4}}>🫙</div>
            <h2 style={{fontFamily:T.displayFont,marginBottom:12}}>{lang==='ar'?'جرّتك فاضية':'Your jar is empty'}</h2>
            <p style={{color:'var(--text-muted)',marginBottom:28}}>{lang==='ar'?'شارك لينكك عشان الناس تملي جرّتك بأضواء.':'Share your link so friends can fill your jar with glows.'}</p>
            <button className="btn btn-glow" onClick={()=>navigate('/inbox')}>{lang==='ar'?'شارك اللينك':'Share my link'}</button>
          </>
        )}

        {phase === 'closed' && (
          <>
            <h2 style={{fontFamily:T.displayFont,fontSize:'clamp(1.4rem,4vw,1.9rem)',marginBottom:12,background:'linear-gradient(135deg,#FFE580,#FFD166)',WebkitBackgroundClip:'text',WebkitTextFillColor:'transparent'}}>
              {lang==='ar'?'ضوك اليومي ✨':'Your daily glow ✨'}
            </h2>
            <p style={{color:'var(--text-muted)',marginBottom:36,lineHeight:1.7}}>
              {lang==='ar'?'اضغط الجرّة عشان تفتحها وتشوف الرسالة اللي اختارالهاlek النهارده':'Tap the jar to reveal today\'s message picked just for you'}
            </p>
            <div style={{position:'relative',display:'inline-block',cursor:'pointer'}} onClick={openJar}>
              <div style={{position:'absolute',inset:-40,borderRadius:'50%',background:'radial-gradient(circle,rgba(255,209,102,0.16),transparent 70%)',animation:'ringPulse 2.5s ease-in-out infinite'}}/>
              <JarSVG fill={jarFill} size={180}/>
              <div style={{position:'absolute',bottom:-8,left:'50%',transform:'translateX(-50%)',fontSize:'0.75rem',color:'var(--gold)',opacity:0.8,whiteSpace:'nowrap'}}>
                {lang==='ar'?'اضغط عشان تفتح':'tap to open'}
              </div>
            </div>
          </>
        )}

        {phase === 'opening' && (
          <>
            <div style={{animation:'jarFloat 0.9s ease-in-out',display:'inline-block'}}>
              <JarSVG fill={jarFill} size={180}/>
            </div>
            <p style={{marginTop:24,color:'var(--gold)',fontSize:'1.1rem',animation:'fadeUp 0.5s ease'}}>
              {lang==='ar'?'✨ بيتفتح…':'✨ Opening…'}
            </p>
          </>
        )}

        {phase === 'open' && todayGlow && (
          <div style={{animation:'fadeUp 0.5s ease'}}>
            {alreadyOpened && (
              <div style={{fontSize:'0.75rem',color:'var(--text-muted)',marginBottom:16,background:'rgba(255,209,102,0.08)',border:'1px solid rgba(255,209,102,0.18)',borderRadius:100,padding:'4px 14px',display:'inline-block'}}>
                {lang==='ar'?'✓ فتحت جرّتك النهارده':'✓ Already opened today'}
              </div>
            )}
            <div style={{fontSize:'3rem',marginBottom:16}}>{todayGlow.emoji}</div>
            <div style={{background:'var(--card)',border:'1px solid rgba(255,209,102,0.22)',borderRadius:20,padding:'28px 32px',marginBottom:24,boxShadow:'0 8px 40px rgba(0,0,0,0.4)'}}>
              <p style={{fontFamily:T.displayFont,fontSize:'clamp(1rem,3vw,1.25rem)',fontStyle:'italic',lineHeight:1.85,color:'var(--text)',margin:0}}>
                "{todayGlow.text}"
              </p>
              <div style={{marginTop:16,display:'flex',alignItems:'center',justifyContent:'center',gap:6,fontSize:'0.75rem',color:'var(--text-muted)'}}>
                <div style={{width:4,height:4,borderRadius:'50%',background:'var(--gold)',opacity:0.5}}/>
                {lang==='ar'?'ضو مجهول':'anonymous glow'}
              </div>
            </div>

            {/* Mood check */}
            <p style={{color:'var(--text-muted)',fontSize:'0.88rem',marginBottom:14}}>
              {lang==='ar'?'إزاي حاسس دلوقتي؟':'How does this make you feel?'}
            </p>
            <div style={{display:'flex',gap:10,justifyContent:'center',marginBottom:28,flexWrap:'wrap'}}>
              {['😊','💛','🥹','🌟','🫂'].map(e=>(
                <button key={e} onClick={()=>{}} style={{fontSize:'1.6rem',background:'var(--bg3)',border:'1px solid var(--border)',borderRadius:12,padding:'10px 14px',cursor:'pointer',transition:'all 0.15s'}}
                  onMouseEnter={ev=>ev.target.style.transform='scale(1.2)'}
                  onMouseLeave={ev=>ev.target.style.transform='scale(1)'}
                >{e}</button>
              ))}
            </div>

            <div style={{display:'flex',gap:10,justifyContent:'center',flexWrap:'wrap'}}>
              <button className="btn btn-ghost" style={{fontSize:'0.85rem'}} onClick={()=>navigate('/inbox')}>
                {lang==='ar'?'شوف كل الأضواء':'See all glows'}
              </button>
              <button className="btn btn-glow" style={{fontSize:'0.85rem'}} onClick={()=>navigate('/inbox/'+todayGlow.id)}>
                {lang==='ar'?'احفظ في المفضلة':'Save to favorites'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div></>
  );
}

function DailyNav({lang, setLang, user, onLogout, navigate}) {
  const T = t[lang];
  return (
    <nav dir={T.dir}>
      <div style={{display:'flex',alignItems:'center',gap:8,cursor:'pointer'}} onClick={()=>navigate('/inbox')}>
        <span style={{fontSize:'1.1rem'}}>←</span>
        <span style={{fontSize:'0.85rem',color:'var(--text-muted)'}}>{T.myJar}</span>
      </div>
      <div style={{fontFamily:T.displayFont,fontSize:'1rem',fontWeight:600,background:'linear-gradient(135deg,#FFE580,#FFD166)',WebkitBackgroundClip:'text',WebkitTextFillColor:'transparent'}}>
        {lang==='ar'?'ضوك اليومي':'Daily Glow'}
      </div>
      <button className="lang-btn" onClick={()=>setLang(l=>l==='en'?'ar':'en')}>{lang==='en'?'عربي':'EN'}</button>
    </nav>
  );
}
